import React, { useContext, useEffect, useRef, useState } from "react";
import Axios from "axios";
import { API_URL } from "../../components/api";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Collect.css";
import MCCheck from "./MCCheck";
import LanguageContext from "../../context/LanguageContext";
import { cleanMoneyInput, withCommas } from "../../../utils/money";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

const previewNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const payloadMoney = (value) =>
  value === "" || value === null ? "0" : String(value);

const displayMoney = (value) => {
  const parsed = Number(value);

  return Number.isFinite(parsed)
    ? parsed.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "0.00";
};

const targetForItem = (item) =>
  item.kind === "legacy"
    ? "legacy"
    : String(item.invoice_id);

const requestErrorMessage = (error, fallback) => {
  const data = error.response?.data;

  return (
    (typeof data === "string" && data) ||
    data?.error ||
    data?.message ||
    data?.detail ||
    error.message ||
    fallback
  );
};

function Collect(props) {
  const { direction, t } = useContext(LanguageContext);
  const clientId = props.client_id;

  const [walletSummary, setWalletSummary] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState("");
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [mode, setMode] = useState("settlement");
  const [exchangeRate, setExchangeRate] = useState("0");
  const [paidUSD, setPaidUSD] = useState("0");
  const [paidLBP, setPaidLBP] = useState("0");
  const [returnedUSD, setReturnedUSD] = useState("0");
  const [returnedLBP, setReturnedLBP] = useState("0");
  const [discountUSD, setDiscountUSD] = useState("0");
  const [discountLBP, setDiscountLBP] = useState("0");
  const [reason, setReason] = useState("");
  const [onlineAccounts, setOnlineAccounts] = useState([]);
  const [onlineAccountId, setOnlineAccountId] = useState("");
  const [showSmallMenu, setShowSmallMenu] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [freeCollectionBusy, setFreeCollectionBusy] = useState(null);
  const [freeCollectionError, setFreeCollectionError] = useState({});
  const [freeCollectionTarget, setFreeCollectionTarget] = useState(null);
  const [authTokens] = useState(() =>
    JSON.parse(localStorage.getItem("authTokens")),
  );

  const outstandingItems = Array.isArray(
    walletSummary?.outstanding_items,
  )
    ? walletSummary.outstanding_items
    : [];

  const balance = Number(walletSummary?.balance_usd);
  const balanceIsValid =
    walletSummary !== null && Number.isFinite(balance);
  const canSettle = walletSummary?.can_settle === true;
  const canDeposit = walletSummary?.can_deposit === true;
  const automaticWalletApplied =
    outstandingItems.reduce(
      (total, item) =>
        total +
        previewNumber(
          item.automatic_wallet_applied_usd,
        ),
      0,
    );

  const operationIdRef = useRef("");
  const submittingRef = useRef(false);

  const fullName =
    walletSummary?.client_name || props.fullName;

  useEffect(() => {
    let active = true;

    setWalletLoading(true);
    setWalletError("");
    setWalletSummary(null);
    setOnlineAccounts([]);
    setSelectedTargets([]);
    setMode("settlement");
    setExchangeRate("0");
    setPaidUSD("0");
    setPaidLBP("0");
    setReturnedUSD("0");
    setReturnedLBP("0");
    setDiscountUSD("0");
    setDiscountLBP("0");
    setReason("");
    setOnlineAccountId("");
    setSubmitError("");
    setShowSmallMenu(false);
    operationIdRef.current = "";

    const loadWallet = async () => {
      try {
        const response = await Axios.get(
          `${API_URL}/accounting/wallet/`,
          {
            headers: {
              Authorization:
                `Bearer ${authTokens?.access}`,
            },
            params: {
              client_id: clientId,
            },
          },
        );

        if (!active) return;

        const summary = response.data;
        const items = Array.isArray(
          summary.outstanding_items,
        )
          ? summary.outstanding_items
          : [];
        const payableItems = items.filter(
          (item) => item.payable,
        );

        if (
          summary.can_settle &&
          payableItems.length === 0
        ) {
          throw new Error(
            "The server did not return a payable "
              + "outstanding item.",
          );
        }

        setWalletSummary(summary);
        setOnlineAccounts(
          Array.isArray(summary.online_accounts)
            ? summary.online_accounts
            : [],
        );
        setMode(
          summary.can_settle
            ? "settlement"
            : "deposit",
        );
        setSelectedTargets(
          payableItems.length === 1
            ? [targetForItem(payableItems[0])]
            : [],
        );
      } catch (error) {
        if (!active) return;

        setWalletSummary(null);
        setSelectedTargets([]);
        setWalletError(
          requestErrorMessage(
            error,
            t("collection.walletLoadFailed"),
          ),
        );
      } finally {
        if (active) {
          setWalletLoading(false);
        }
      }
    };

    loadWallet();

    return () => {
      active = false;
    };
  }, [clientId, authTokens?.access]);

  const requestFreeCollection = async (item) => {
    if (freeCollectionBusy !== null) return;

    setFreeCollectionTarget(null);
    setFreeCollectionBusy(item.invoice_id);
    setFreeCollectionError({});

    try {
      await Axios.post(
        `${API_URL}/accounting/collection-requests/`,
        { invoice_id: item.invoice_id },
        {
          headers: {
            Authorization: `Bearer ${authTokens?.access}`,
          },
        },
      );

      setWalletSummary((current) =>
        current
          ? {
              ...current,
              outstanding_items: (
                current.outstanding_items || []
              ).map((row) =>
                row.invoice_id === item.invoice_id
                  ? {
                      ...row,
                      free_collection_status: "pending",
                    }
                  : row,
              ),
            }
          : current,
      );
    } catch (error) {
      setFreeCollectionError({
        [item.invoice_id]: requestErrorMessage(
          error,
          t("collection.freeCollectionFailed"),
        ),
      });
    } finally {
      setFreeCollectionBusy(null);
    }
  };

  const collectClose = () => props.handleClose();

  const invalidateOperation = () => {
    operationIdRef.current = "";
    setSubmitError("");
  };

  const changeValue = (setter) => (event) => {
    invalidateOperation();
    setter(event.target.value);
  };

  const changeMoney = (setter) => (event) =>
    changeValue(setter)({
      target: { value: cleanMoneyInput(event.target.value) },
    });

  const handleModeChange = (nextMode) => {
    if (submittingRef.current) return;

    invalidateOperation();
    setMode(nextMode);
    setExchangeRate("0");
    setPaidUSD("0");
    setPaidLBP("0");
    setReturnedUSD("0");
    setReturnedLBP("0");
    setDiscountUSD("0");
    setDiscountLBP("0");
    setReason("");
    setOnlineAccountId("");

    if (
      nextMode === "settlement" &&
      selectedTargets.length === 0
    ) {
      const firstPayable = outstandingItems.find(
        (item) => item.payable,
      );

      if (firstPayable) {
        setSelectedTargets([
          targetForItem(firstPayable),
        ]);
      }
    }
  };

  const handleInvoiceSelect = (item) => {
    if (
      submittingRef.current ||
      !item.payable
    ) {
      return;
    }

    invalidateOperation();

    const target = targetForItem(item);

    setSelectedTargets((current) =>
      current.includes(target)
        ? current.filter(
            (value) => value !== target,
          )
        : [...current, target],
    );
  };

  const handleSelectAll = () => {
    if (submittingRef.current) return;

    invalidateOperation();

    const targets = outstandingItems
      .filter((item) => item.payable)
      .map((item) => targetForItem(item));

    const allSelected =
      targets.length > 0 &&
      targets.every((target) =>
        selectedTargets.includes(target),
      );

    setSelectedTargets(
      allSelected ? [] : targets,
    );
  };

  const rate = previewNumber(exchangeRate);
  const paidUsdValue = previewNumber(paidUSD);
  const paidLbpValue = previewNumber(paidLBP);
  const returnedUsdValue = previewNumber(returnedUSD);
  const returnedLbpValue = previewNumber(returnedLBP);
  const discountUsdValue = previewNumber(discountUSD);
  const discountLbpValue = previewNumber(discountLBP);

  const usesLBP =
    paidLbpValue !== 0 ||
    returnedLbpValue !== 0 ||
    (mode === "settlement" && discountLbpValue !== 0);

  const validRate = rate > 0;

  const cashValueUSD =
    paidUsdValue -
    returnedUsdValue +
    (validRate
      ? (paidLbpValue - returnedLbpValue) / rate
      : 0);

  const discountValueUSD =
    mode === "settlement"
      ? discountUsdValue +
        (validRate ? discountLbpValue / rate : 0)
      : 0;

  const selectedItems = outstandingItems.filter(
    (item) =>
      item.payable &&
      selectedTargets.includes(
        targetForItem(item),
      ),
  );

  const selectedTotal = selectedItems.reduce(
    (total, item) =>
      total +
      previewNumber(item.remaining_due_usd),
    0,
  );

  const totalOutstanding = previewNumber(
    walletSummary?.amount_due_usd,
  );

  const currentWalletCredit = previewNumber(
    walletSummary?.wallet_credit_usd,
  );

  const totalSettlementValue =
    cashValueUSD + discountValueUSD;

  const settlementValueForPreview = Math.max(
    totalSettlementValue,
    0,
  );

  const hasSelection =
    selectedTargets.length > 0;

  const appliedToSelected = hasSelection
    ? Math.min(
        settlementValueForPreview,
        selectedTotal,
      )
    : 0;

  const overflowAfterSelected = hasSelection
    ? Math.max(
        settlementValueForPreview -
          appliedToSelected,
        0,
      )
    : 0;

  const otherOutstanding = Math.max(
    totalOutstanding - selectedTotal,
    0,
  );

  const appliedToOther = hasSelection
    ? Math.min(
        overflowAfterSelected,
        otherOutstanding,
      )
    : 0;

  const totalRemaining = hasSelection
    ? Math.max(
        totalOutstanding -
          appliedToSelected -
          appliedToOther,
        0,
      )
    : totalOutstanding;

  const walletCreditAdded = hasSelection
    ? Math.max(
        cashValueUSD -
          totalOutstanding,
        0,
      )
    : 0;

  const isPartialSettlement =
    mode === "settlement" &&
    hasSelection &&
    settlementValueForPreview > 0 &&
    totalRemaining > 0;

  const walletCreditAfterDeposit =
    currentWalletCredit + cashValueUSD;

  const payableTargets = outstandingItems
    .filter((item) => item.payable)
    .map((item) => targetForItem(item));

  const allPayableSelected =
    payableTargets.length > 0 &&
    payableTargets.every((target) =>
      selectedTargets.includes(target),
    );

  let projectedText = t("collection.walletUnavailable");

  if (walletLoading) {
    projectedText = t("collection.walletLoading");
  } else if (usesLBP && !validRate) {
    projectedText = t("collection.walletEnterExchangeRate");
  } else if (mode === "deposit") {
    projectedText = Number.isFinite(
      walletCreditAfterDeposit,
    )
      ? displayMoney(walletCreditAfterDeposit)
      : t("collection.walletInvalidAmount");
  } else if (!hasSelection) {
    projectedText = t("collection.walletSelectOutstanding");
  } else {
    projectedText = Number.isFinite(
      totalRemaining,
    )
      ? displayMoney(totalRemaining)
      : t("collection.walletInvalidAmount");
  }

  const handlePostRequest = async () => {
    if (submittingRef.current) return;

    if (walletLoading) {
      setSubmitError(
        t("collection.walletDetailsStillLoading"),
      );
      return;
    }

    if (walletError) {
      setSubmitError(walletError);
      return;
    }

    if (!balanceIsValid) {
      setSubmitError("The client balance is unavailable.");
      return;
    }

    if (mode === "settlement" && !canSettle) {
      setSubmitError(
        t("collection.walletSettlementRequiresBalance"),
      );
      return;
    }

    if (
      mode === "settlement" &&
      selectedTargets.length === 0
    ) {
      setSubmitError(
        t("collection.walletSelectPayable"),
      );
      return;
    }

    if (mode === "deposit" && !canDeposit) {
      setSubmitError(
        "The client must settle all outstanding "
          + "amounts before adding Wallet credit.",
      );
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError("");

    try {
      if (!operationIdRef.current) {
        if (
          !window.crypto ||
          typeof window.crypto.getRandomValues !== "function"
        ) {
          throw new Error(
            t("collection.walletSecureOperationUnsupported"),
          );
        }

        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);

        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;

        const hex = Array.from(
          bytes,
          (value) =>
            value.toString(16).padStart(2, "0"),
        );

        operationIdRef.current = [
          hex.slice(0, 4).join(""),
          hex.slice(4, 6).join(""),
          hex.slice(6, 8).join(""),
          hex.slice(8, 10).join(""),
          hex.slice(10, 16).join(""),
        ].join("-");
      }

      const requestData = {
        client_id: clientId,
        mode,
        operation_id: operationIdRef.current,
        paid_usd: payloadMoney(paidUSD),
        paid_lbp: payloadMoney(paidLBP),
        returned_usd: payloadMoney(returnedUSD),
        returned_lbp: payloadMoney(returnedLBP),
        discount_usd:
          mode === "settlement"
            ? payloadMoney(discountUSD)
            : "0",
        discount_lbp:
          mode === "settlement"
            ? payloadMoney(discountLBP)
            : "0",
        exchange_rate: payloadMoney(exchangeRate),
        online_account_id: onlineAccountId,
        reason: reason.trim(),
      };

      if (mode === "settlement") {
        requestData.invoice_ids =
          selectedTargets.map((target) =>
            target === "legacy"
              ? "legacy"
              : Number(target),
          );
      }

      const response = await Axios.post(
        `${API_URL}/accounting/wallet/`,
        requestData,
        {
          headers: {
            Authorization:
              `Bearer ${authTokens.access}`,
          },
        },
      );

      operationIdRef.current = "";
      setExchangeRate("0");
      setPaidUSD("0");
      setPaidLBP("0");
      setReturnedUSD("0");
      setReturnedLBP("0");
      setDiscountUSD("0");
      setDiscountLBP("0");
      setReason("");
      setOnlineAccountId("");

      const receiptData = {
        ...(response.data || {}),
        fullname_en: props.fullName,
        balance_record: props.balance_usd,
        paid_amount_usd: requestData.paid_usd,
        paid_amount_lbp: requestData.paid_lbp,
        exchange_rate: requestData.exchange_rate,
        balance_usd:
          response.data &&
          response.data.balance_usd !== undefined
            ? response.data.balance_usd
            : props.balance_usd,
        discount_usd: requestData.discount_usd,
        discount_lbp: requestData.discount_lbp,
      };

      collectClose();
      props.handleOpen(receiptData);
      props.fetch();

      if (requestData.online_account_id) {
        window.dispatchEvent(
          new Event("oss-online-drawer-updated")
        );
      }
    } catch (error) {
      const data = error.response?.data;

      setSubmitError(
        (typeof data === "string" && data) ||
          data?.error ||
          data?.message ||
          data?.detail ||
          error.message ||
          t("collection.walletOperationFailed"),
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="collect-container accounting-collect-form">
      <h5 className="collect-heading">
        {t("collection.walletClient")} {fullName || clientId}
      </h5>

      <div
        className="btn-group w-100 mb-3"
        role="group"
        dir={direction}
        aria-label={t("collection.walletOperation")}
      >
        <button
          type="button"
          className={
            mode === "settlement"
              ? "btn btn-primary"
              : "btn btn-outline-primary"
          }
          onClick={() => handleModeChange("settlement")}
          disabled={submitting}
        >
          {t("collection.walletSettlement")}
        </button>
        <button
          type="button"
          className={
            mode === "deposit"
              ? "btn btn-primary"
              : "btn btn-outline-primary"
          }
          onClick={() => handleModeChange("deposit")}
          disabled={submitting}
        >
          {t("collection.walletDeposit")}
        </button>
      </div>

      {!(mode === "settlement" && canSettle) && (
        <div className="alert alert-info py-2">
          {mode === "settlement"
            ? t("collection.walletNoOutstanding")
            : canDeposit
              ? t("collection.walletDepositHelp")
              : t("collection.walletSettleBeforeCredit")}
        </div>
      )}

      {walletLoading && (
        <div className="alert alert-secondary" role="status">
          {t("collection.walletLoadingDetails")}
        </div>
      )}

      {walletError && (
        <div className="alert alert-danger" role="alert">
          {walletError}
        </div>
      )}

      {!walletLoading &&
        !walletError &&
        !balanceIsValid && (
        <div className="alert alert-danger" role="alert">
          {t("collection.walletBalanceUnavailable")}
        </div>
      )}

      <label className="collect-label">
        {mode === "deposit"
          ? t("collection.walletCurrentCredit")
          : t("collection.walletTotalDue")}
        <input
          type="text"
          value={
            walletSummary
              ? displayMoney(
                  mode === "deposit"
                    ? walletSummary.wallet_credit_usd
                    : walletSummary.amount_due_usd,
                )
              : t("collection.walletUnavailable")
          }
          readOnly
          className="form-control collect-input darker-bg"
        />
      </label>

      {mode === "settlement" &&
        canSettle && (
          <div className="mb-3">
            {automaticWalletApplied > 0 && (
              <div className="mb-2">
                <strong>
                  {t("collection.walletAutomaticallyApplied", {
                    amount: displayMoney(automaticWalletApplied),
                  })}
                </strong>
              </div>
            )}

              <h6>
                {t("collection.walletOutstandingInvoices")}
              </h6>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="mb-0 d-flex align-items-center">
                  <input
                    type="checkbox"
                    checked={allPayableSelected}
                    onChange={handleSelectAll}
                    disabled={
                      submitting ||
                      payableTargets.length === 0
                    }
                    style={{
                      marginInlineEnd: "8px",
                    }}
                  />

                  {t("collection.walletSelectAll")}
                </label>

                <strong>
                  {t("collection.walletSelectedTotal")}
                  {": $"}
                  {displayMoney(selectedTotal)}
                </strong>
              </div>

              <Dialog
                open={freeCollectionTarget !== null}
                onClose={() => setFreeCollectionTarget(null)}
                disableEnforceFocus
                PaperProps={{
                  style: {
                    backgroundColor: "#31495A",
                    color: "#F1F5F7",
                  },
                }}
              >
                <div dir={direction}>
                  <DialogTitle>
                    {t("collection.freeCollection")}
                  </DialogTitle>
                  <DialogContent>
                    {freeCollectionTarget !== null &&
                      t("collection.freeCollectionConfirm", {
                        id: freeCollectionTarget.invoice_id,
                      })}
                  </DialogContent>
                  <DialogActions>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light"
                      onClick={() => setFreeCollectionTarget(null)}
                    >
                      {t("collection.freeCollectionCancel")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      disabled={freeCollectionBusy !== null}
                      onClick={() =>
                        requestFreeCollection(freeCollectionTarget)
                      }
                    >
                      {t("collection.freeCollectionSend")}
                    </button>
                  </DialogActions>
                </div>
              </Dialog>

              <div className="list-group">
                {outstandingItems.map((item) => {
                  const target =
                    targetForItem(item);

                  const selected =
                    selectedTargets.includes(
                      target,
                    );

                  const title =
                    item.kind === "legacy"
                      ? t(
                          "collection.walletLegacyOutstanding",
                        )
                      : t(
                          "collection.walletInvoiceNumber",
                          {
                            id: item.invoice_id,
                          },
                        );

                  return (
                    <label
                      key={target}
                      className={
                        selected
                          ? "wallet-invoice-option list-group-item active"
                          : "wallet-invoice-option list-group-item"
                      }
                      style={{
                        backgroundColor: "#31495A",
                        borderColor: "#607786",
                        color: "#F1F5F7",
                        cursor:
                          item.payable && !submitting
                            ? "pointer"
                            : "not-allowed",
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={
                            !item.payable ||
                            submitting
                          }
                          onChange={() =>
                            handleInvoiceSelect(item)
                          }
                          style={{
                            marginInlineEnd: "10px",
                            marginTop: "4px",
                          }}
                        />

                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between">
                            <strong>{title}</strong>

                            <strong>
                              ${displayMoney(
                                item.remaining_due_usd,
                              )}
                            </strong>
                          </div>

                          {item.date && (
                            <small className="d-block">
                              {t(
                                "collection.walletDate",
                              )}{" "}
                              {new Date(
                                item.date,
                              ).toLocaleDateString()}
                            </small>
                          )}

                          <div className="mt-1">
                            {item.description ||
                              t(
                                "collection.walletNoDescription",
                              )}
                          </div>

                          {item.kind === "invoice" && (
                            <small className="d-block mt-1">
                              {t(
                                "collection.walletOriginalAmount",
                              )}
                              {displayMoney(
                                item.original_amount_usd,
                              )}

                              {" | "}

                              {t(
                                "collection.walletInvoiceDiscount",
                              )}
                              {displayMoney(
                                item.invoice_discount_usd,
                              )}

                              {" | "}

                              {t(
                                "collection.walletReceiptsApplied",
                              )}
                              {displayMoney(
                                item.linked_applied_usd,
                              )}
                            </small>
                          )}

                          {props.freeCollection === true &&
                            item.kind === "invoice" &&
                            item.collection_fee === true &&
                            item.free_collection_enabled === true && (
                              <div className="mt-2">
                                {item.free_collection_status ===
                                "pending" ? (
                                  <small className="d-block">
                                    {t(
                                      "collection.freeCollectionPending",
                                    )}
                                  </small>
                                ) : item.free_collection_status ===
                                  "refused" ? (
                                  <small className="d-block">
                                    {t(
                                      "collection.freeCollectionRefused",
                                    )}
                                  </small>
                                ) : String(item.status || "")
                                    .toLowerCase() === "pending" &&
                                  Number(item.linked_receipt_count) ===
                                    0 &&
                                  Number(item.remaining_due_usd) ===
                                    Number(item.net_amount_usd) ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-light"
                                    disabled={
                                      submitting ||
                                      freeCollectionBusy !== null
                                    }
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setFreeCollectionTarget(item);
                                    }}
                                  >
                                    {t("collection.freeCollection")}
                                  </button>
                                ) : null}

                                {freeCollectionError[
                                  item.invoice_id
                                ] && (
                                  <small className="d-block text-warning mt-1">
                                    {
                                      freeCollectionError[
                                        item.invoice_id
                                      ]
                                    }
                                  </small>
                                )}
                              </div>
                            )}

                          {!item.payable && (
                            <small className="d-block mt-1">
                              {t(
                                "collection.walletSettleEarlier",
                              )}
                            </small>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
          </div>
        )}

      <div className="row collect-form">
        <div className="col-md-6">
          <label className="collect-label">
            {mode === "deposit"
              ? t("collection.walletDepositUsd")
              : t("collection.walletPaymentUsd")}
            <input
              type="text"
              inputMode="decimal"
              value={withCommas(paidUSD)}
              onChange={changeMoney(setPaidUSD)}
              onWheel={(event) => event.target.blur()}
              disabled={submitting}
              className="form-control collect-input darker-bg"
            />
          </label>

          <label className="collect-label">
            {t("collection.walletChangeUsd")}
            <input
              type="text"
              inputMode="decimal"
              value={withCommas(returnedUSD)}
              onChange={changeMoney(setReturnedUSD)}
              onWheel={(event) => event.target.blur()}
              disabled={submitting}
              className="form-control collect-input darker-bg"
            />
          </label>

          {mode === "settlement" && (
            <label className="collect-label">
              {t("collection.walletDiscountUsd")}
              <input
                type="text"
                inputMode="decimal"
                value={withCommas(discountUSD)}
                onChange={changeMoney(setDiscountUSD)}
                onWheel={(event) => event.target.blur()}
                disabled={submitting}
                className="form-control collect-input darker-bg"
              />
            </label>
          )}
        </div>

        <div className="col-md-6">
          <label className="collect-label">
            {t("collection.walletExchangeRate")}
            <input
              type="text"
              inputMode="decimal"
              value={withCommas(exchangeRate)}
              onChange={changeMoney(setExchangeRate)}
              onWheel={(event) => event.target.blur()}
              disabled={submitting}
              className="form-control collect-input"
            />
          </label>

          <label className="collect-label">
            {mode === "deposit"
              ? t("collection.walletDepositLbp")
              : t("collection.walletPaymentLbp")}
            <input
              type="text"
              inputMode="decimal"
              value={withCommas(paidLBP)}
              onChange={changeMoney(setPaidLBP)}
              onWheel={(event) => event.target.blur()}
              disabled={submitting}
              className="form-control collect-input darker-bg"
            />
          </label>

          <label className="collect-label">
            {t("collection.walletChangeLbp")}
            <input
              type="text"
              inputMode="decimal"
              value={withCommas(returnedLBP)}
              onChange={changeMoney(setReturnedLBP)}
              onWheel={(event) => event.target.blur()}
              disabled={submitting}
              className="form-control collect-input darker-bg"
            />
          </label>

          {mode === "settlement" && (
            <label className="collect-label">
              {t("collection.walletDiscountLbp")}
              <input
                type="text"
                inputMode="decimal"
                value={withCommas(discountLBP)}
                onChange={changeMoney(setDiscountLBP)}
                onWheel={(event) => event.target.blur()}
                disabled={submitting}
                className="form-control collect-input darker-bg"
              />
            </label>
          )}
        </div>
      </div>

      <div className="collect-reason">
        <label className="collect-label">
          {t("collection.walletReason")}
          <input
            type="text"
            value={reason}
            onChange={changeValue(setReason)}
            disabled={submitting}
            className="form-control collect-input darker-bg"
          />
        </label>
      </div>

      {mode === "settlement" &&
        canSettle && (
          <div className="collect-form">
            <div className="d-flex justify-content-between mb-1">
              <span>
                {t("collection.walletSelectedTotal")}
              </span>
              <strong>
                ${displayMoney(selectedTotal)}
              </strong>
            </div>

            <div className="d-flex justify-content-between mb-1">
              <span>
                {t("collection.walletTotalOutstanding")}
              </span>
              <strong>
                ${displayMoney(totalOutstanding)}
              </strong>
            </div>

            {usesLBP && !validRate ? (
              <small>
                {t("collection.walletEnterExchangeRate")}
              </small>
            ) : (
              <>
                <div className="d-flex justify-content-between mb-1">
                  <span>
                    {t("collection.walletPaymentTotal")}
                  </span>
                  <strong>
                    ${displayMoney(cashValueUSD)}
                  </strong>
                </div>

                <div className="d-flex justify-content-between mb-1">
                  <span>
                    {t("collection.walletDiscountTotal")}
                  </span>
                  <strong>
                    ${displayMoney(discountValueUSD)}
                  </strong>
                </div>

                <div className="d-flex justify-content-between mb-1">
                  <span>
                    {t("collection.walletAppliedSelected")}
                  </span>
                  <strong>
                    ${displayMoney(appliedToSelected)}
                  </strong>
                </div>

                <div className="d-flex justify-content-between mb-1">
                  <span>
                    {t("collection.walletAppliedOther")}
                  </span>
                  <strong>
                    ${displayMoney(appliedToOther)}
                  </strong>
                </div>

                <div className="d-flex justify-content-between mb-1">
                  <span>
                    {t("collection.walletTotalRemaining")}
                  </span>
                  <strong>
                    ${displayMoney(totalRemaining)}
                  </strong>
                </div>

                <div className="d-flex justify-content-between">
                  <span>
                    {t("collection.walletCreditAdded")}
                  </span>
                  <strong>
                    ${displayMoney(walletCreditAdded)}
                  </strong>
                </div>

                {isPartialSettlement && (
                  <small className="d-block mt-2">
                    {t("collection.walletPartialPayment")}
                  </small>
                )}
              </>
            )}
          </div>
        )}

      <label className="collect-label">
        {mode === "deposit"
          ? t("collection.walletCreditAfterDeposit")
          : t("collection.walletRemainingAfterSettlement")}
        <input
          type="text"
          value={projectedText}
          readOnly
          className="form-control collect-input darker-bg"
        />
      </label>

      <div className="collect-reason">
        <label className="collect-label">
          {t("collection.onlinePaymentMethod")}
          <select
            value={onlineAccountId}
            onChange={changeValue(setOnlineAccountId)}
            disabled={submitting}
            className="form-control collect-input darker-bg"
          >
            <option value="">—</option>
            {onlineAccounts.map((account) => (
              <option
                key={account.online_account_id}
                value={String(account.online_account_id)}
              >
                {account.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {submitError && (
        <div className="alert alert-danger" role="alert">
          {submitError}
        </div>
      )}

      <div className="collect-submit">
        <button
          type="button"
          onClick={handlePostRequest}
          disabled={
            submitting ||
            walletLoading ||
            Boolean(walletError) ||
            !balanceIsValid ||
            (mode === "settlement" && !canSettle) ||
            (mode === "deposit" && !canDeposit)
          }
          className="btn btn-primary"
        >
          {submitting
            ? t("collection.walletSubmitting")
            : mode === "settlement"
              ? t("collection.walletSettleSelected")
              : t("collection.walletAddCredit")}
        </button>
      </div>

      <div className="small-menu-toggle">
        <button
          type="button"
          onClick={() => setShowSmallMenu(!showSmallMenu)}
          className="btn btn-secondary mt-3"
          disabled={submitting}
        >
          {showSmallMenu
            ? t("collection.walletHide")
            : t("collection.walletCheckShield")}
        </button>
        {showSmallMenu && (
          <MCCheck client_id={clientId} />
        )}
      </div>
    </div>
  );
}

export default Collect;
